import React, { useEffect } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';

// Import all full pages from /src/pages directory
import Loading from '../../pages/loading';
import Login from '../../pages/login';
import TerminalLogin from '../../pages/terminal-login';
import NotFound from '../../pages/not-found';
import Products from '../../pages/products';
import Lookup from '../../pages/lookup';
import Search from '../../pages/search';
import SearchResults from '../../pages/search-results';
import Layout from '../layout';
import Cart from '../../pages/cart';
import Checkout from '../../pages/checkout';
import OrderDetails from '../../pages/order-details';
import OrdersIndex from '../../pages/orders-index';
import Closeout from '../../pages/closeout';
import CashDrop from '../../pages/cash-drop';
import DrawerHistoryIndex from '../../pages/drawer-history-index';
import CashActionDetails from '../../pages/cash-action-details';
import Reporting from '../../pages/reporting';
import { LoginCallback, Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '../../pages/login/Login.state';
import {
  getAccessToken,
  getDecodedAccessToken,
  getRefreshToken,
} from '../../utils/state';
import { OKTA_OAUTH_ERROR } from '../../utils/constants';
import Logout from '../../pages/logout/Logout';
import { logout } from '../../state/reducer';

const AllRoutes = ({ hasTerminalSession }) => {
  const config = useSelector((state) => state.config);

  const oktaAuth = new OktaAuth(config.oidc);
  const isLoggedIn = Boolean(getRefreshToken()) && Boolean(getAccessToken());
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    if (isLoggedIn) {
      const { first_name, last_name, email } = getDecodedAccessToken();
      dispatch(
        loginSuccess({
          cashier: {
            first_name,
            last_name,
            email,
          },
        }),
      );
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      location.pathname === config.oidc.redirectUri &&
      location.search.includes(OKTA_OAUTH_ERROR.queryStr)
    ) {
      dispatch(logout({ navigate }));
    }
  }, [location.pathname]);

  const restoreOriginalUri = (_oktaAuth, originalUri) =>
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  const routes = useRoutes([
    {
      path: '/pos',
      element:
        isLoggedIn && hasTerminalSession ? (
          <Layout
            isLoggedIn={isLoggedIn}
            hasTerminalSession={hasTerminalSession}
          />
        ) : (
          <Navigate to="/login" />
        ),
      children: [
        {
          path: '/pos/cashdrop',
          element: <CashDrop />,
        },
        {
          path: '/pos/closeout',
          element: <Closeout />,
        },
        {
          path: '/pos/results',
          element: <SearchResults />,
        },
        {
          path: '/pos/lookup/:id',
          element: <Lookup />,
        },
        {
          path: '/pos/search/:id',
          element: <Search />,
        },
        {
          path: '/pos/products',
          element: <Products />,
        },
        {
          path: '/pos/cart',
          element: <Cart />,
        },
        {
          path: '/pos/checkout',
          element: <Checkout />,
        },
        {
          path: '/pos/order/:id',
          element: <OrderDetails />,
        },
        {
          path: '/pos/cashaction/:id',
          element: <CashActionDetails />,
        },
        {
          path: '/pos/closeout/:id',
          element: <CashActionDetails />,
        },
        {
          path: '/pos/orders',
          element: <OrdersIndex />,
        },
        {
          path: '/pos/reporting',
          element: <Reporting />,
        },
        {
          path: '/pos/drawer-history',
          element: <DrawerHistoryIndex />,
        },
        {
          path: '/pos',
          element: <Navigate to="/pos/products" />,
        },
        { path: '/pos/loading', element: <Loading /> },
        { path: '/pos/not-found', element: <NotFound /> },
      ],
    },
    {
      path: '/login/callback',
      element: (
        <LoginCallback oktaAuth={oktaAuth} loadingElement={<Loading />} />
      ),
    },
    {
      path: '/login',
      element:
        !isLoggedIn || !hasTerminalSession ? (
          <Layout isLoggedIn={isLoggedIn} />
        ) : (
          <Navigate to="/pos" />
        ),
      children: [
        {
          path: '/login/user',
          element:
            isLoggedIn && !hasTerminalSession ? (
              <Navigate to="/login/terminal" />
            ) : (
              <Login />
            ),
        },
        {
          path: '/login/terminal',
          element: isLoggedIn ? (
            <TerminalLogin />
          ) : (
            <Navigate to={`/login/user`} />
          ),
        },
        {
          path: '/login',
          element: <Navigate to={`/login/user`} />,
        },
      ],
    },
    {
      path: '/logout',
      element: <Logout />,
    },
    {
      path: '/',
      element: <Navigate to="/login" />,
    },
  ]);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {routes}
    </Security>
  );
};

export default AllRoutes;
